/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { Popover } from "@material-ui/core";
import { Block, Close } from "@material-ui/icons";
import { useI18n } from "hooks/i18n/i18n";
import { modalActions } from "modules/modal";
import React from "react";
import { useDispatch } from "react-redux";
import { Button } from "ui/chakra/components/Button";

import { getFullscreenContainer } from "./useFullscreen";

import type { UserMessage } from "@spwn/types/firebase/firestore";

type Props = {
  target: React.RefObject<HTMLElement>;
  targetComment: Pick<UserMessage, "uid" | "name"> | undefined;
  onClose: () => void;
  onClickBlockUser: (
    comment: Pick<UserMessage, "uid" | "name">
  ) => Promise<void>;
};

const menuStyle = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
});

const menuItemStyle = css({
  height: "40px",
  padding: "0 12px",
  columnGap: "4px",
  justifyContent: "flex-start",
  fontSize: "14px",
  lineHeight: "20px",
  fontWeight: 500,
  borderRadius: 0,
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
});

const blockUserItemStyle = css([menuItemStyle, { color: "#9b2c2c" }]);

/**
 * 一般ユーザーが自分の表示からコメントを消すためのメニュー。
 * 管理者の BAN 操作は StreamingCommentBanDialog。
 */
export const StreamingCommentBlockDialog: React.FC<Props> = ({
  target,
  targetComment,
  onClose,
  onClickBlockUser,
}) => {
  const dispatch = useDispatch();
  const { t } = useI18n();

  if (!targetComment) return null;

  return (
    <Popover
      open
      container={getFullscreenContainer()}
      onBackdropClick={onClose}
      anchorEl={target.current}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{ style: { borderRadius: 8 } }}
    >
      <div css={menuStyle}>
        <Button
          size="md"
          variant="ghost"
          onClick={async () => {
            try {
              await onClickBlockUser(targetComment);
            } catch {
              dispatch(
                modalActions.toggleError({
                  msg: t("streaming.commentBlock.blockError"),
                })
              );
            } finally {
              // 失敗しても閉じる。開きっぱなしだとエラー表示が隠れる。
              onClose();
            }
          }}
          leftIcon={<Block />}
          css={blockUserItemStyle}
        >
          {t("streaming.commentBlock.blockUser")}
        </Button>
        <Button
          size="md"
          variant="ghost"
          onClick={onClose}
          leftIcon={<Close />}
          css={menuItemStyle}
        >
          {t("streaming.commentBlock.close")}
        </Button>
      </div>
    </Popover>
  );
};
